import {
  Modal,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Box,
  ModalBody,
  Flex,
  Text,
  VStack,
  InputGroup,
  InputRightElement,
  Input,
  IconButton,
  Stack,
  Skeleton,
  Button,
  Tag,
  TagLabel,
  TagCloseButton,
} from '@chakra-ui/react';

import { v4 as uuidv4 } from 'uuid';

import { useDispatch, useSelector } from 'react-redux';

import { ReducerStatus } from 'utils/constants';

import ResultModal from 'components/common/modals/ResultModal';
import { learningPlanCourseActions } from 'store/modules/learningPlanCourse/learningPlanCourse-slice';
import {
  AddIcon,
  ArrowBackIcon,
  ArrowForwardIcon,
  CloseIcon,
  SearchIcon,
} from '@chakra-ui/icons';
import { useEffect, useMemo, useState } from 'react';
import { fetchCoursesAction } from 'store/modules/course/course-actions';
import debounce from 'lodash.debounce';
import { fetchCourseQuestsAction } from 'store/modules/quest/quest-actions';
import QuestAdminItem from 'components/quest/QuestAdminItem';
import { addLearningPlanCoursesAction } from 'store/modules/learningPlanCourse/learningPlanCourse-action';
import CourseToAdd from '../CourseToAdd';

function AddLearningPlanCoursesMd() {
  const dispatch = useDispatch();

  const courses = useSelector((state) => state.course.courses);

  const metaData = useSelector((state) => state.course.metaData);
  const getStatus = useSelector((state) => state.course.status);

  const addLearningPlanCourses = useSelector(
    (state) => state.learningPlanCourse.addLearningPlanCourses
  );
  const learningPlanCourses = useSelector(
    (state) => state.learningPlanCourse.learningPlanCourses
  );

  const courseQuests = useSelector((state) => state.quest.courseQuests);
  const getQuestsStatus = useSelector((state) => state.quest.getQuestsStatus);

  const [page, setPage] = useState(1);
  const [query, setQuery] = useState('');
  const [searchText, setSearchText] = useState('');
  const [selectedCourses, setSelectedCourses] = useState([]);
  const [previewId, setPreviewId] = useState(null);

  const learningPlanCourseIds = new Set(
    (learningPlanCourses || []).map(
      (learningPlanCourse) => learningPlanCourse.course?.id
    )
  );

  const filteredCourses = (courses || []).filter(
    (course) => !learningPlanCourseIds.has(course.id)
  );

  const handleToggleModal = (isOpen) => {
    dispatch(
      learningPlanCourseActions.setAddLearningPlanCourses({
        modal: isOpen,
        id: null,
      })
    );
  };

  const handleCheckboxChange = (courseId, isChecked) => {
    if (isChecked) {
      setSelectedCourses([...selectedCourses, courseId]);
    } else {
      setSelectedCourses(selectedCourses.filter((id) => id !== courseId));
    }
  };

  const handlePreview = (selectedCourseId) => {
    setPreviewId(selectedCourseId);
    dispatch(fetchCourseQuestsAction(selectedCourseId));
  };

  const handleResultButton = () => {
    dispatch(
      learningPlanCourseActions.setAddLearningPlanCourses({
        status: ReducerStatus.IDLE,
      })
    );
    if (addLearningPlanCourses.status === ReducerStatus.SUCCEEDED) {
      setSelectedCourses([]);
      setPreviewId(null);

      dispatch(
        learningPlanCourseActions.setAddLearningPlanCourses({
          modal: false,
          id: null,
          status: ReducerStatus.IDLE,
          error: null,
        })
      );
    }
  };

  const handleAddCourses = () => {
    if (selectedCourses.length === 0) {
      return;
    }

    const coursesData = selectedCourses.map((courseId) => ({
      learningPlanId: addLearningPlanCourses.id,
      courseId,
    }));

    dispatch(addLearningPlanCoursesAction(coursesData));
  };

  const debouncedFetchData = useMemo(
    () =>
      debounce((textValue) => {
        setQuery(textValue);
      }, 350),
    [setQuery]
  );

  const handleSearchTextChange = (e) => {
    setSearchText(e.target.value);
    debouncedFetchData(e.target.value);
  };

  useEffect(() => {
    dispatch(
      fetchCoursesAction({
        currentPage: query ? 1 : page,
        pageSize: 10,
        query,
      })
    );
  }, [page, query, dispatch]);

  return (
    <Box>
      {addLearningPlanCourses.status === ReducerStatus.SUCCEEDED ||
      addLearningPlanCourses.status === ReducerStatus.FAILED ? (
        <ResultModal
          isOpen={addLearningPlanCourses.modal}
          onSubmit={handleResultButton}
          isError={addLearningPlanCourses.status === ReducerStatus.FAILED}
          title={
            addLearningPlanCourses.status === ReducerStatus.SUCCEEDED
              ? 'Course(s) Added'
              : null
          }
          message={
            addLearningPlanCourses.status === ReducerStatus.SUCCEEDED
              ? 'The selected course(s) has been added successfully.'
              : addLearningPlanCourses.error?.data?.title || 'Server Error'
          }
          errorObject={addLearningPlanCourses.error?.data?.errors}
        />
      ) : (
        <Modal
          size="full"
          isOpen={addLearningPlanCourses.modal}
          onClose={() => handleToggleModal(false)}
        >
          <ModalOverlay />
          <ModalContent>
            <ModalHeader>Add Courses</ModalHeader>
            <ModalCloseButton />

            <ModalBody>
              <Flex
                wrap="wrap"
                align="stretch"
                direction={{ base: 'column', md: 'row' }}
                gap={5}
              >
                <Box
                  flex="1"
                  border="1px"
                  borderColor="gray.200"
                  boxShadow="base"
                  p={5}
                >
                  <VStack gap={3} align="stretch">
                    <Text as="samp" fontSize="xs">
                      <strong>SELECT COURSES</strong>
                    </Text>

                    <Flex wrap="wrap" gap={2}>
                      <InputGroup flex={1}>
                        <InputRightElement pointerEvents="none">
                          <SearchIcon color="gray.300" />
                        </InputRightElement>
                        <Input
                          name="query"
                          placeholder="Search Courses"
                          value={searchText}
                          onChange={handleSearchTextChange}
                        />
                      </InputGroup>

                      <Button
                        isLoading={
                          addLearningPlanCourses.status ===
                          ReducerStatus.LOADING
                        }
                        onClick={handleAddCourses}
                        leftIcon={<AddIcon />}
                        colorScheme="red"
                      >
                        Add
                      </Button>
                    </Flex>

                    {selectedCourses.length > 0 && (
                      <Box>
                        <Text mb={2}>Selected ID:</Text>

                        <Flex gap={2}>
                          {selectedCourses.map((courseId) => (
                            <Tag
                              key={uuidv4()}
                              size="sm"
                              borderRadius="full"
                              variant="solid"
                              colorScheme="blue"
                            >
                              <TagLabel>{courseId}</TagLabel>
                              <TagCloseButton
                                onClick={() =>
                                  handleCheckboxChange(courseId, false)
                                }
                              />
                            </Tag>
                          ))}
                        </Flex>
                      </Box>
                    )}

                    {getStatus === ReducerStatus.LOADING ? (
                      <Stack gap={4}>
                        {[...Array(5)].map(() => (
                          <Skeleton
                            key={uuidv4()}
                            w="100%"
                            height="50px"
                            borderRadius="md"
                          />
                        ))}
                      </Stack>
                    ) : (
                      filteredCourses?.map((course) => (
                        <CourseToAdd
                          key={course.id}
                          data={course}
                          handleCheckboxChange={handleCheckboxChange}
                          handlePreview={handlePreview}
                          checkValue={
                            selectedCourses.find((id) => id === course.id) !=
                            null
                          }
                        />
                      ))
                    )}

                    {getStatus === ReducerStatus.SUCCEEDED &&
                      metaData &&
                      metaData?.totalItems !== (courses || []).length && (
                        <Flex
                          mt={5}
                          mx={1}
                          gap={2}
                          justify="center"
                          align="center"
                        >
                          <IconButton
                            size="sm"
                            colorScheme="blue"
                            isDisabled={page - 1 <= 0}
                            aria-label="previous"
                            onClick={() => setPage(page - 1)}
                            icon={<ArrowBackIcon />}
                          />

                          <IconButton
                            size="sm"
                            colorScheme="blue"
                            aria-label="next"
                            isDisabled={!metaData?.hasNext}
                            onClick={() => setPage(page + 1)}
                            icon={<ArrowForwardIcon />}
                          />
                        </Flex>
                      )}
                  </VStack>
                </Box>

                <Box
                  flex="1"
                  p={5}
                  boxShadow="xs"
                  minH={230}
                  overflowY="scroll"
                >
                  <VStack h="100%" gap={3} align="stretch">
                    <Flex align="center">
                      <Text flex={1} as="samp" fontSize="xs">
                        <strong>COURSE PREVIEW</strong>
                      </Text>

                      {previewId && (
                        <IconButton
                          onClick={() => setPreviewId(null)}
                          size="xs"
                          variant="ghost"
                          icon={<CloseIcon />}
                        />
                      )}
                    </Flex>

                    {getQuestsStatus === ReducerStatus.LOADING ? (
                      <Stack align="stretch">
                        <Skeleton mb={6} height="72px" borderRadius="md" />
                        <Skeleton mb={6} height="72px" borderRadius="md" />
                        <Skeleton mb={6} height="72px" borderRadius="md" />
                        <Skeleton mb={6} height="72px" borderRadius="md" />
                      </Stack>
                    ) : (
                      <Box>
                        {previewId &&
                          courseQuests.map((courseQuest) => (
                            <QuestAdminItem
                              key={courseQuest.id}
                              courseId={previewId}
                              id={courseQuest.id}
                              courseQuest={courseQuest}
                              isPreviewMode
                            />
                          ))}
                      </Box>
                    )}

                    {!previewId && (
                      <Flex w="100%" h="100%" justify="center" align="center">
                        <Text fontSize="xl" color="gray.500">
                          Toggle over the courses to get a preview of its
                          content
                        </Text>
                      </Flex>
                    )}
                  </VStack>
                </Box>
              </Flex>
            </ModalBody>
          </ModalContent>
        </Modal>
      )}
    </Box>
  );
}

export default AddLearningPlanCoursesMd;
